import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Row } from 'antd';

import FormItem from '@/components/form/FormItem';
import { renderBasicFieldControl } from '@/components/form/SchemaForm/renderBasicFieldControl';
import { SPACE_MD } from '@/theme/buildAntdTheme';

import type {
  FieldConfig,
  FieldType,
} from '@/components/form/SchemaForm/types';
import type { ColProps, FormItemProps } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { FormInstance } from 'antd/es/form';
import type {
  FormListFieldData,
  FormListOperation,
} from 'antd/es/form/FormList';
import type { DefaultOptionType } from 'antd/es/select';
import type { ReactNode } from 'react';

export type DynamicFieldListRowContext = {
  field: FormListFieldData;
  index: number;
  remove: FormListOperation['remove'];
  fields: FormListFieldData[];
};

type DynamicFieldColSpan = Pick<
  ColProps,
  'span' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'offset' | 'flex'
>;

type DynamicListFieldType = Exclude<FieldType, 'list' | 'custom' | 'file'>;

export type DynamicFieldItemConfig = {
  /** Path relative to the list item — DynamicFieldList prepends field.name */
  name: string | (string | number)[];
  colSpan?: DynamicFieldColSpan;
  /** Shown only on the first row unless alwaysShowLabel is true */
  label?: ReactNode;
  /** Shown only on the first row unless alwaysShowLabel is true */
  tooltip?: FormItemProps['tooltip'];
  help?: FormItemProps['help'];
  /** When true, label and tooltip are shown on every row */
  alwaysShowLabel?: boolean;
  /** Declarative control type — uses SchemaForm field registry with inputSize */
  fieldType?: DynamicListFieldType;
  placeholder?: string;
  options?: DefaultOptionType[];
  componentProps?: Record<string, unknown>;
  /** Custom control when fieldType is not sufficient (e.g. filtered Select) */
  input?: ReactNode;
};

export type DynamicFieldRawConfig = {
  raw: true;
  colSpan?: DynamicFieldColSpan;
  content: ReactNode;
};

export type DynamicFieldConfig = DynamicFieldItemConfig | DynamicFieldRawConfig;

export type DynamicFieldListProps = {
  name: string | (string | number)[];
  renderRow: (ctx: DynamicFieldListRowContext) => DynamicFieldConfig[];
  addLabel?: string;
  min?: number;
  defaultItem?: Record<string, unknown>;
  onRemove?: (index: number, field: FormListFieldData) => void;
  isRowRemovable?: (index: number, field: FormListFieldData) => boolean;
  showAddButton?: boolean;
  gutter?: number | [number, number];
  rowWrapper?: (row: ReactNode) => ReactNode;
  inputSize?: SizeType;
  form?: FormInstance;
};

const isRawConfig = (
  config: DynamicFieldConfig,
): config is DynamicFieldRawConfig => 'raw' in config && config.raw === true;

const toNamePath = (
  fieldName: number,
  name: string | (string | number)[],
): (string | number)[] => {
  const relativeName = Array.isArray(name) ? name : [name];
  return [fieldName, ...relativeName];
};

const renderRowControl = (
  config: DynamicFieldItemConfig,
  inputSize: SizeType,
  form: FormInstance,
): ReactNode => {
  if (config.input != null) {
    return config.input;
  }

  if (config.fieldType != null) {
    const syntheticField: FieldConfig = {
      type: config.fieldType,
      ...(typeof config.label === 'string' ? { label: config.label } : {}),
      placeholder: config.placeholder,
      options: config.options,
      componentProps: config.componentProps,
    };
    return renderBasicFieldControl(syntheticField, {
      form,
      field: syntheticField,
      inputSize,
    });
  }

  return null;
};

const DynamicFieldList = ({
  name,
  renderRow,
  addLabel,
  min = 1,
  defaultItem = {},
  onRemove,
  isRowRemovable,
  showAddButton = true,
  gutter = SPACE_MD,
  rowWrapper,
  inputSize = 'middle',
  form: formProp,
}: DynamicFieldListProps) => {
  const formInstance = Form.useFormInstance();
  const form = formProp ?? formInstance;

  return (
    <Flex
      vertical
      gap={0}
      style={{ marginBottom: 'var(--ant-form-item-margin-bottom)' }}
    >
      <Form.List name={name}>
        {(fields, { add, remove }) => (
          <Flex vertical gap={SPACE_MD}>
            <Flex vertical>
              {fields.map((field, index) => {
                const configs = renderRow({
                  field,
                  index,
                  remove: (removeIndex) => {
                    if (typeof removeIndex === 'number') {
                      onRemove?.(removeIndex, field);
                      remove(removeIndex);
                    }
                  },
                  fields,
                });

                const row = (
                  <Row gutter={gutter} style={{ flex: 1 }}>
                    {configs.map((config, configIndex) => (
                      <Col
                        key={configIndex}
                        {...(config.colSpan ?? { span: 24 })}
                      >
                        {isRawConfig(config) ? (
                          config.content
                        ) : (
                          <FormItem
                            name={toNamePath(field.name, config.name)}
                            label={
                              config.alwaysShowLabel || index === 0
                                ? config.label
                                : undefined
                            }
                            tooltip={
                              config.alwaysShowLabel || index === 0
                                ? config.tooltip
                                : false
                            }
                            help={config.help}
                            normalize={
                              config.fieldType === 'number'
                                ? (value: unknown) =>
                                    value === '' || value === undefined
                                      ? null
                                      : value
                                : undefined
                            }
                            style={{ marginBottom: rowWrapper ? undefined : 0 }}
                          >
                            {renderRowControl(config, inputSize, form)}
                          </FormItem>
                        )}
                      </Col>
                    ))}
                  </Row>
                );

                const canRemove =
                  index >= min && (isRowRemovable?.(index, field) ?? true);

                return (
                  <Flex key={field.key} align="center">
                    {rowWrapper ? rowWrapper(row) : row}
                    <Flex
                      style={{
                        visibility: canRemove ? 'visible' : 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!canRemove}
                        onClick={() => {
                          if (!canRemove) {
                            return;
                          }
                          onRemove?.(index, field);
                          remove(index);
                        }}
                      />
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
            {showAddButton && (
              <Flex align="flex-start">
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => add(defaultItem)}
                  icon={<PlusOutlined />}
                >
                  {addLabel}
                </Button>
              </Flex>
            )}
          </Flex>
        )}
      </Form.List>
    </Flex>
  );
};

export default DynamicFieldList;
